import { css } from '@emotion/react'
import { LivePortraitFragment } from 'api/contentful/contentful.graphql.types'
import { animateUserMemory, postUserMemory, putReferralMemoryUuid } from 'api/goodtrust/user/memory'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { DIDLicense } from 'components/content/memory/didLicense/DIDLicense'

import {
  MemoriesHeroContent,
  MemoriesHeroContentBox,
  MemoriesHeroContentWrapper,
  ShareItemsContainer,
  SInstructiveText,
  SMemoriesHeroGallery,
  SMemoriesRedo,
  SUploadingWrapper,
} from './MemoryHero.styled'
import { isPictureTooBig, MEMORY_SIZE_LIMIT } from 'components/content/memory/utils'
import { useFakeProgressBar } from 'components/fakeProgressBar/FakeProgressBar'
import { Icon } from 'components/icon'
import { Image } from 'components/Image'
import { useChooseAnimationModal } from 'components/memory/chooseAnimationModal/ChooseAnimationModal'
import { usePaywallMemory } from 'components/memory/MemoryPaywallUtils'
import { SelectedMemory } from 'components/memory/MemoryUtils'
import { useAuthModal } from 'components/modal/authModal/AuthModal'
import { handleAndToastError, toastError, toastErrorString, toastSuccess } from 'components/Toast'
import { Text, Title, Title_h1_default } from 'components/Typography'
import { UserMemoryCarousel } from 'components/videoCarousel/MemoryCarousel'
import { VideoCarousel } from 'components/videoCarousel/VideoCarousel'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Router from 'next/router'
import { ReactElement } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { fireEvent } from 'utils/analytics'
import { ApiError } from 'utils/error'
import { unwrapResponse } from 'utils/fetcher'
import { logError } from 'utils/general'
import { useLoadingCallback, useWindowSize } from 'utils/hooks'
import { useStoredPromoCodeLogic } from 'utils/promo'
import {
  clearSessionValue,
  getSessionValue,
  SESSION_REFERRAL_MEMORY_UUID,
} from 'utils/sessionStorage'
import { GenericAsyncFunction } from 'utils/types'

export function MemoryHero(props: {
  title: string
  desc: string
  memoryActions?: ReactElement
  shareItems?: ReactElement
  memories?: SelectedMemory[]
  previewAnimations?: LivePortraitFragment[]
  isPublic?: boolean
  onRedo?: GenericAsyncFunction | null
  className?: string
}) {
  const { t } = useTranslation()
  const { t: tMemory } = useTranslation('memory')

  const storedPromoLogic = useStoredPromoCodeLogic()
  const [chooseAnimationModal, chooseAnimationViaModal] = useChooseAnimationModal()

  const [authModal, authWithModal, closeAuthModal] = useAuthModal({
    onLogin: () => fireEvent('photo_upload_signup', { action: 'logged_in' }),
    onSignup: async () => {
      try {
        fireEvent('photo_upload_signup', { action: 'signed_up' })
        const referralMemoryUuid = getSessionValue(SESSION_REFERRAL_MEMORY_UUID)
        if (referralMemoryUuid) {
          await putReferralMemoryUuid(referralMemoryUuid).then(unwrapResponse)
          clearSessionValue(SESSION_REFERRAL_MEMORY_UUID)
        }
      } catch (err) {
        logError('Memory referral failed: ', err)
      }
    },
    onCancel: () => {
      fireEvent('photo_upload_signup', { action: 'aborted' })
      setTimeout(() => window.location.reload())
    },
  })

  const { hitMemoryPaywallWithRedirect } = usePaywallMemory()

  const size = useWindowSize()
  const [fakeProgressBar, progressFakeProgressBar] = useFakeProgressBar()

  const [upload, _isUploading, wasUploading] = useLoadingCallback(async (file: File) => {
    fireEvent('photo_uploaded', {
      page: 'memories_landing',
      photo_source: 'file_upload',
    })
    await progressFakeProgressBar(50, 4000)
    authWithModal(async () => {
      closeAuthModal()
      progressFakeProgressBar(70, 2000)

      await storedPromoLogic?.onRegistered()

      try {
        const uploadRes = await postUserMemory(file).then(unwrapResponse)
        if (!uploadRes.json?.uuid) throw new ApiError(uploadRes)

        const driver = await chooseAnimationViaModal()
        if (!driver) {
          toastSuccess('animation_cancelled')
          return
        }

        progressFakeProgressBar(90, 2000)

        const animateRes = await animateUserMemory(uploadRes.json.uuid, {
          driver_type: driver,
        })
        if (!animateRes.ok) {
          if (animateRes.errorCode === 'user_exceeded_free_quota') {
            await progressFakeProgressBar(0, 500)
            await hitMemoryPaywallWithRedirect({
              memoryUuid: uploadRes.json.uuid,
              driver,
            })
            return
          } else {
            throw new ApiError(animateRes)
          }
        }

        await progressFakeProgressBar(100, 1000)
        await Router.push(`/memories/${uploadRes.json?.uuid}/animating`)
      } catch (err) {
        await progressFakeProgressBar(0, 500)
        handleAndToastError(err)
      }
    })
  })

  const [redo, isRedoing] = useLoadingCallback(async () => await props.onRedo?.())

  const { getRootProps, getInputProps, isDragActive, inputRef, acceptedFiles } = useDropzone({
    maxSize: MEMORY_SIZE_LIMIT,
    accept: 'image/png,image/jpeg',
    multiple: false,
    noClick: true,
    onDrop: async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        toastErrorString(
          fileRejections
            .map((file) =>
              file.errors
                .map((err) => {
                  switch (err.code) {
                    case 'file-invalid-type': {
                      return t('common.error_codes.file_invalid_type_image')
                    }
                    case 'file-too-large': {
                      return t('common.error_codes.image_too_large')
                    }
                    case 'too-many-files': {
                      return t('common.error_codes.pick_single_image')
                    }
                    default:
                      // return err.message
                      return t('common.error_codes.something_went_wrong')
                  }
                })
                .join(',')
            )
            .join('\n')
        )
      } else if (acceptedFiles.length) {
        const file = acceptedFiles[0]
        if (file) {
          isPictureTooBig(file).then((isTooBig) => {
            if (isTooBig) {
              toastError('image_10mp_limit')
            } else {
              upload(file)
            }
          })
        }
      }
    },
  })

  return (
    <Block className={props.className}>
      {chooseAnimationModal}
      {storedPromoLogic?.jsx}
      <Head>
        {props.previewAnimations?.map((animation) => {
          return (
            <link
              rel="preload"
              as="image"
              href={animation?.photo?.url as string}
              key={animation?.photo?.url}
            />
          )
        })}
      </Head>
      {authModal}
      <MemoriesHeroContentWrapper>
        <MemoriesHeroContent
          {...getRootProps({
            isDragActive,
            isAccepting: !wasUploading,
          })}
        >
          <MemoriesHeroContentBox>
            <input
              ref={inputRef}
              {...getInputProps()}
              id="photo-upload-button"
              data-testid="upload-photo"
            />

            <Title variant={Title_h1_default} css={{ marginBottom: '1.5rem' }}>
              {props.title}
            </Title>

            <Text css={{ marginBottom: '1.6875rem' }}>{props.desc}</Text>
            {props.memoryActions ? (
              props.memoryActions
            ) : (
              <>
                <Button
                  type="button"
                  tone="accent"
                  size="large"
                  css={{ marginBottom: 'var(--p-button)' }}
                  block={[true, false]}
                  onClick={() => document.getElementById('photo-upload-button')?.click()}
                  leftIcon={<Icon name="FileUploadFrame" />}
                >
                  {tMemory('memory.live.action_button')}
                </Button>
                <SInstructiveText className="desktop-only">
                  {tMemory('memory.live.instructive_text')}
                </SInstructiveText>
                {!size.isDesktop && <DIDLicense />}
              </>
            )}
            {size.isDesktop && (
              <DIDLicense
                css={css`
                  position: absolute;
                  top: 103%;
                  bottom: 0;
                  right: -3.5%;
                `}
              />
            )}
          </MemoriesHeroContentBox>
          <ShareItemsContainer>{props.shareItems}</ShareItemsContainer>
        </MemoriesHeroContent>

        <SMemoriesHeroGallery isUploading={wasUploading}>
          {wasUploading ? (
            <SUploadingWrapper>
              {fakeProgressBar}
              <Text css={{ fontWeight: 'bold', color: 'black' }}>
                {tMemory('memory.live.uploading_text')}
              </Text>
              <Text>{acceptedFiles[0]?.name}</Text>
            </SUploadingWrapper>
          ) : props.previewAnimations ? (
            <VideoCarousel disableAudio items={props.previewAnimations} />
          ) : props.memories?.length && !props.memories[0]?.selectedAnimation ? (
            <Image
              css={css`
                width: 100%;
                max-height: 100%;
              `}
              src={props.memories[0]?.original_file?.url}
            />
          ) : (
            <UserMemoryCarousel memories={props.memories} isPublic={props.isPublic} />
          )}

          {props.onRedo && (
            <SMemoriesRedo>
              <Text>{tMemory('memory.live.redo_text')}</Text>
              <Button
                type="button"
                variant="text"
                leftIcon={<Icon name="StarWand" />}
                onClick={redo}
                loading={isRedoing}
              >
                {tMemory('memory.live.redo_button')}
              </Button>
            </SMemoriesRedo>
          )}
        </SMemoriesHeroGallery>
      </MemoriesHeroContentWrapper>
    </Block>
  )
}
